import { useState, useEffect, useCallback } from 'react';

export interface TrackingData {
    session: string;
    url: string;
    viewportWidth: number;
    viewportHeight: number;
    screenWidth: number;
    screenHeight: number;
    screenColorDepth: number;
    screenDevicePixelRatio: number;
    userAgent: string;
    language: string;
    platform: string;
    hardwareConcurrency: number;
    deviceMemory?: number;
    timezone: string;
    referrer: string;
    touchPoints: number;
    connectionEffectiveType?: string;
    connectionDownlink?: number;
    connectionRtt?: number;
    batteryLevel?: number;
    batteryCharging?: boolean;
    ip?: string;
    country?: string;
    city?: string;
    longitude?: string;
    latitude?: string;
    region?: string;
    regionCode?: string;
    metroCode?: string;
    postalCode?: string;
    cfRay?: string;
}

/**
 * A hook that collects client-side specific tracking data.
 * Note: Some properties (like battery or connection) may be undefined 
 * depending on browser support and privacy settings.
 */
export const useTracking = (): TrackingData => {
    const storedTrack = sessionStorage.getItem("track");
    const trackData = JSON.parse(storedTrack ?? "{}");
    const [trackingData, setTrackingData] = useState<TrackingData>({
        url: typeof window !== 'undefined' ? window.location.href : '',
        viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
        viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
        screenWidth: typeof window !== 'undefined' ? window.screen.width : 0,
        screenHeight: typeof window !== 'undefined' ? window.screen.height : 0,
        screenColorDepth: typeof window !== 'undefined' ? window.screen.colorDepth : 0,
        screenDevicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 0,
        userAgent: typeof window !== 'undefined' ? navigator.userAgent : '',
        language: typeof window !== 'undefined' ? navigator.language : '',
        platform: typeof window !== 'undefined' ? navigator.platform : '',
        hardwareConcurrency: typeof window !== 'undefined' ? navigator.hardwareConcurrency : 0,
        deviceMemory: typeof window !== 'undefined' ? (navigator as any).deviceMemory : undefined,
        timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        touchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
        connectionEffectiveType: undefined,
        connectionDownlink: undefined,
        connectionRtt: undefined,
        batteryLevel: undefined,
        batteryCharging: undefined,
        ...trackData
    });

    const updateViewport = useCallback(() => {
        if (typeof window !== 'undefined') {
        setTrackingData((prev) => ({
            ...prev,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        }));
        }
    }, []);

    const updateUrl = useCallback(() => {
        if (typeof window !== 'undefined') {
        setTrackingData((prev) => ({
            ...prev,
            url: window.location.href,
        }));
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Handle viewport changes
        window.addEventListener('resize', updateViewport);
        window.addEventListener('popstate', updateUrl);
        window.addEventListener('hashchange', updateUrl);
        
        // Handle connection changes if supported
        const connection = (navigator as any).connection;
        const updateConnection = () => {
        if (connection) {
            setTrackingData((prev) => ({
            ...prev,
            connection_effective_type: connection.effectiveType,
            connection_downlink: connection.downlink,
            connection_rtt: connection.rtt,
            }));
        }
        };
        
        if (connection) {
        connection.addEventListener('change', updateConnection);
        }

        // Handle battery status if supported
        let battery: any = null;
        const updateBattery = () => {
        if (battery) {
            setTrackingData((prev) => ({
            ...prev,
            battery_level: battery.level,
            battery_charging: battery.charging,
            }));
        }
        };

        if ('getBattery' in navigator) {
        (navigator as any).getBattery().then((batt: any) => {
            battery = batt;
            updateBattery();
            batt.addEventListener('levelchange', updateBattery);
            batt.addEventListener('chargingchange', updateBattery);
        });
        }


        // Initial update
        updateViewport();
        updateUrl();
        if (connection) updateConnection();

        return () => {
        window.removeEventListener('resize', updateViewport);
        window.removeEventListener('popstate', updateUrl);
        window.removeEventListener('hashchange', updateUrl);
        if (connection) {
            connection.removeEventListener('change', updateConnection);
        }
        if (battery) {
            battery.removeEventListener('levelchange', updateBattery);
            battery.removeEventListener('chargingchange', updateBattery);
        }
        };
    }, [updateViewport, updateUrl]);

    return trackingData;
};
